import { useTranslation } from 'react-i18next';

import { ActionButton } from '@/components/actions-button/actions-button';
import { useActionPanel } from '@/features/action-panel/use-action-panel';
import { AskXButton } from '@/features/ask-x/ask-x-button';
import { useProjectActionsInfo } from '@/features/projects/action-panel/use-project-actions-info.query';
import { useExportHandlers } from '@/features/projects/hooks/use-export-handlers';
import { useProject } from '@/features/projects/overview/use-project.query';
import { projectIdState } from '@/features/projects/project.state';
import { modalState } from '@/state/modal.state';
import { Button } from '@/ui/button/button';
import { IconButton } from '@/ui/button/icon-button';
import type { DropdownOption } from '@/ui/dropdown/dropdown';
import { WithDropdown } from '@/ui/dropdown/dropdown';
import { Inline } from '@/ui/line/line';
import { MenuButton } from '@/ui/menu-button/menu-button';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';
import { useCurrentUser } from '@/user/use-current-user.query';
import { useProjectSidebar } from '@/features/projects/overview/sidebar/use-project-sidebar';
import { canCopyProjectToAccount } from '@/features/projects/copy-to-account/can-copy-project-to-account';
import { useExportToCSV } from '@/features/projects/export-to-csv/use-export-to-csv.mutation';
import { useSetAtom, useAtomValue } from 'jotai';
import { useCreateFeedFromProject } from '../project-header/use-create-feed-from-project';
import { NotificationsButton } from '@/features/notifications/notifications-button';

export const ProjectHeaderActions = () => {
  const { t } = useTranslation('projects');
  const { data: projectActions } = useProjectActionsInfo();
  const { hasRequiredPermission } = useUserPermissions();
  const { hasRequiredProduct } = useUserProducts();
  const { data: userData } = useCurrentUser();
  const { handleChangeToggleProjectSidebar } = useProjectSidebar();
  const projectId = useAtomValue(projectIdState);
  const { data: project } = useProject(projectId);
  const setModalState = useSetAtom(modalState);
  const { mutateAsync: exportToCSV } = useExportToCSV();
  const { handleExportToPpt } = useExportHandlers();
  const { toggleActionPanel } = useActionPanel();
  const { createFeedFromProject } = useCreateFeedFromProject();
  if (!project) return null;

  const onToggleActionPanelClick = () =>
    toggleActionPanel({ context: 'project', title: project.name });

  const openShareProject = () => {
    setModalState({ state: 'shareProject' });
  };

  const hasPermissionToShareProject =
    hasRequiredPermission(PERMISSION.SHARE_A_STREAM_TO_EXISTING_Q_USERS) ||
    project.isOwnedByUser;
  const hasOtherAccounts =
    (userData?.userAccounts ?? []).some(
      ({ account }) => account.id !== userData?.account.id,
    ) ?? false;
  const hasCopyToAccountAccess = canCopyProjectToAccount({
    hasCopyProjectProduct: hasRequiredProduct(
      PRODUCT.COPY_PROJECT_TO_ANOTHER_ACCOUNT,
    ),
    hasDuplicateStreamPermission: hasRequiredPermission(
      PERMISSION.DUPLICATE_STREAM,
    ),
    hasOtherAccounts,
  });

  const menu: DropdownOption[] = [
    {
      label: t`share`,
      isVisible: hasPermissionToShareProject,
      onClick: openShareProject,
      icon: 'ArrowUpFromSquare',
    },
    {
      label: t`duplicate`,
      isVisible: hasRequiredPermission(PERMISSION.DUPLICATE_STREAM),
      onClick: () => setModalState({ state: 'duplicateProject' }),
      icon: 'Copy',
    },
    {
      label: t`addToFeed`,
      isVisible: hasRequiredPermission(PERMISSION.MANAGE_FEED),
      onClick: createFeedFromProject,
      icon: 'FourSquares1Turned',
    },
    {
      label: t`copyToAccount.menuItemLabel`,
      isVisible: hasCopyToAccountAccess,
      onClick: () => {
        if (!hasCopyToAccountAccess) {
          return;
        }

        setModalState({
          state: 'copyProjectModal',
          projectId: project.id,
        });
      },
      icon: 'CopyTo',
    },
    {
      label: t`loadCompaniesFromExcel`,
      isVisible: hasRequiredPermission(PERMISSION.ADD_COMPANIES_TO_PROJECTS),
      onClick: () =>
        setModalState({
          state: 'loadCompaniesToProject',
          projectId: project.id,
        }),
    },
    {
      label: t`exportToCsv`,
      isVisible:
        hasRequiredProduct(PRODUCT.EXPORT_STREAM) &&
        hasRequiredPermission(PERMISSION.EXPORT_PROJECT),
      onClick: () =>
        project &&
        exportToCSV({
          projectId: project.id,
          projectName: project.name,
        }),
      isDisabled: project.listingsCount === 0,
      isTooltipVisible: project.listingsCount === 0,
      tooltipContent: t`addCompaniesToExport`,
    },
    {
      label: t`exportToPpt`,
      isVisible: hasRequiredProduct(PRODUCT.EXPORT_TO_PPT),
      onClick: handleExportToPpt,
    },
    {
      label: t`deleteProject`,
      isVisible: hasRequiredPermission(PERMISSION.DELETE_PROJECT),
      onClick: () =>
        setModalState({ state: 'deleteProject', projectId: project.id }),
      icon: 'Bin',
    },
  ];

  return (
    <Inline alignItems="center" gap="20px">
      <NotificationsButton sizeVariant="small" />
      <AskXButton />

      {hasPermissionToShareProject && (
        <IconButton
          dataTestId="share-project-button"
          onClick={openShareProject}
          icon="ArrowUpFromSquare"
          variant="ghost"
          size="16px"
        />
      )}
      <ActionButton
        totalValue={projectActions?.total || 0}
        onClick={onToggleActionPanelClick}
      />
      <Button onClick={handleChangeToggleProjectSidebar}>{t`moreInfo`}</Button>
      <WithDropdown items={menu} options={{ placement: 'bottom-end' }}>
        <Button variant="text">
          <MenuButton />
        </Button>
      </WithDropdown>
    </Inline>
  );
};
